import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../SafeIcon';
import {
  FiX, FiImage, FiTrash2, FiUploadCloud, FiCheck, FiFolder,
  FiAlertCircle, FiGrid, FiList, FiPackage, FiZoomIn, FiDownload,
  FiCopy, FiChevronLeft, FiChevronRight, FiCheckCircle,
} from 'react-icons/fi';
import supabase from '../../supabase/supabase';

const SUBFOLDERS = ['', 'images', 'logos', 'backgrounds'];

function formatFileSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// ── Full-screen preview modal ─────────────────────────────────────────────────
const ImagePreviewModal = ({ image, images, onClose, onDelete, onNavigate, onSelect, mode }) => {
  const [copied, setCopied] = useState(false);
  const currentIndex = images.findIndex(img => img.id === image.id);

  const handleCopyUrl = () => {
    if (image.url) {
      navigator.clipboard.writeText(image.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handlePrev = (e) => {
    e.stopPropagation();
    if (currentIndex > 0) onNavigate(images[currentIndex - 1]);
  };

  const handleNext = (e) => {
    e.stopPropagation();
    if (currentIndex < images.length - 1) onNavigate(images[currentIndex + 1]);
  };

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'ArrowLeft') handlePrev(e);
      if (e.key === 'ArrowRight') handleNext(e);
      if (e.key === 'Escape') onClose();
      if (e.key === 'Enter' && mode === 'select' && onSelect) {
        onSelect(image);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [currentIndex, images]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/90 backdrop-blur-md p-4"
      onClick={onClose}
    >
      {currentIndex > 0 && (
        <button
          onClick={handlePrev}
          className="absolute left-4 top-1/2 -translate-y-1/2 z-10 bg-white/10 hover:bg-white/20 text-white p-3 rounded-full transition-all"
        >
          <SafeIcon icon={FiChevronLeft} className="w-6 h-6" />
        </button>
      )}
      {currentIndex < images.length - 1 && (
        <button
          onClick={handleNext}
          className="absolute right-4 top-1/2 -translate-y-1/2 z-10 bg-white/10 hover:bg-white/20 text-white p-3 rounded-full transition-all"
        >
          <SafeIcon icon={FiChevronRight} className="w-6 h-6" />
        </button>
      )}

      <motion.div
        key={image.id}
        initial={{ scale: 0.92, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.92, opacity: 0 }}
        className="relative flex flex-col items-center max-w-5xl w-full max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Top bar */}
        <div className="w-full flex items-center justify-between mb-3 px-1">
          <div className="flex-1 min-w-0 mr-4">
            <p className="font-bold text-white truncate text-sm">{image.name}</p>
            <p className="text-xs text-white/50 mt-0.5">
              {currentIndex + 1} / {images.length}
              {image.size && ` · ${formatFileSize(image.size)}`}
            </p>
          </div>
          <div className="flex items-center space-x-2 shrink-0">
            {/* USE THIS IMAGE button — shown in select mode */}
            {mode === 'select' && onSelect && (
              <button
                onClick={() => onSelect(image)}
                className="flex items-center space-x-1.5 px-4 py-2 rounded-lg text-sm font-black bg-[#c8f542] hover:bg-[#d4ff50] text-black transition-all shadow-lg"
              >
                <SafeIcon icon={FiCheck} className="w-4 h-4" />
                <span>Use This Image</span>
              </button>
            )}
            {image.url && (
              <button
                onClick={handleCopyUrl}
                className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                  copied ? 'bg-green-500 text-white' : 'bg-white/10 hover:bg-white/20 text-white'
                }`}
              >
                <SafeIcon icon={copied ? FiCheck : FiCopy} className="w-3.5 h-3.5" />
                <span>{copied ? 'Copied!' : 'Copy URL'}</span>
              </button>
            )}
            {image.url && (
              <a
                href={image.url}
                download={image.name}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-1.5 px-3 py-1.5 bg-white/10 hover:bg-white/20 text-white rounded-lg text-xs font-bold transition-colors"
              >
                <SafeIcon icon={FiDownload} className="w-3.5 h-3.5" />
                <span>Download</span>
              </a>
            )}
            {mode === 'manage' && onDelete && (
              <button
                onClick={() => onDelete(image)}
                className="flex items-center space-x-1.5 px-3 py-1.5 bg-red-500/80 hover:bg-red-500 text-white rounded-lg text-xs font-bold transition-colors"
              >
                <SafeIcon icon={FiTrash2} className="w-3.5 h-3.5" />
                <span>Delete</span>
              </button>
            )}
            <button
              onClick={onClose}
              className="p-1.5 hover:bg-white/20 rounded-lg text-white transition-colors"
            >
              <SafeIcon icon={FiX} className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Image */}
        <div
          className="w-full flex items-center justify-center bg-black/40 rounded-2xl overflow-hidden"
          style={{ maxHeight: '75vh' }}
        >
          {image.url ? (
            <img
              src={image.url}
              alt={image.name}
              className="max-w-full max-h-[75vh] object-contain"
              style={{ display: 'block' }}
            />
          ) : (
            <div className="flex flex-col items-center space-y-3 text-white/30 py-20">
              <SafeIcon icon={FiPackage} className="w-16 h-16" />
              <p className="text-sm font-medium">No preview available</p>
            </div>
          )}
        </div>

        {/* Select hint in select mode */}
        {mode === 'select' && (
          <p className="text-white/40 text-xs mt-3 font-medium">
            Press <kbd className="bg-white/10 px-1.5 py-0.5 rounded text-white/60">Enter</kbd> or click "Use This Image" to select
          </p>
        )}
      </motion.div>
    </motion.div>
  );
};

// ── Main Library Popup ────────────────────────────────────────────────────────
const LibraryPopup = ({ onClose, onSelect, mode = 'select' }) => {
  const [images, setImages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedImages, setSelectedImages] = useState([]);
  const [viewMode, setViewMode] = useState('grid');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);

  useEffect(() => {
    loadImages();
  }, []);

  const loadImages = async () => {
    setIsLoading(true);
    try {
      const allFiles = [];
      const seen = new Set();
      for (const folder of SUBFOLDERS) {
        const { data, error } = await supabase.storage
          .from('media')
          .list(folder || '', { limit: 200, sortBy: { column: 'created_at', order: 'desc' } });
        if (error || !data) continue;
        const validFiles = data.filter(
          file => file.name && !file.name.startsWith('.') && file.id && !seen.has(file.id)
        );
        for (const file of validFiles) {
          seen.add(file.id);
          const filePath = folder ? `${folder}/${file.name}` : file.name;
          const { data: urlData } = supabase.storage.from('media').getPublicUrl(filePath);
          allFiles.push({
            id: file.id,
            name: file.name,
            path: filePath,
            folder: folder || 'root',
            url: urlData?.publicUrl || null,
            created_at: file.created_at,
            size: file.metadata?.size,
          });
        }
      }
      setImages(allFiles);
    } catch (err) {
      console.error('Failed to load library:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (image) => {
    setIsDeleting(true);
    try {
      const { error } = await supabase.storage.from('media').remove([image.path]);
      if (error) throw error;
      setImages(prev => prev.filter(img => img.id !== image.id));
      setDeleteConfirm(null);
      if (previewImage?.id === image.id) setPreviewImage(null);
    } catch (err) {
      console.error('Delete failed:', err);
      alert('Failed to delete image. It may be in use.');
    } finally {
      setIsDeleting(false);
    }
  };

  // ── Immediate select: clicking an image in select mode confirms it ──────────
  const handleImageClick = (image) => {
    if (mode === 'select') {
      // Immediately call onSelect and close
      if (onSelect) onSelect(image);
      onClose();
    } else {
      // manage mode: open full-screen preview
      setPreviewImage(image);
    }
  };

  // Called from the preview modal's "Use This Image" button
  const handleSelectFromPreview = (image) => {
    if (onSelect) onSelect(image);
    onClose();
  };

  const handleConfirm = () => {
    if (onSelect) {
      onSelect(mode === 'single' ? selectedImages[0] : selectedImages);
    }
    onClose();
  };

  return (
    <>
      <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/60 backdrop-blur-md p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          className="bg-white rounded-[2rem] shadow-2xl w-full max-w-4xl overflow-hidden flex flex-col max-h-[85vh]"
        >
          {/* Header */}
          <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/80 shrink-0">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-teal-100 rounded-2xl">
                <SafeIcon icon={FiFolder} className="w-6 h-6 text-teal-600" />
              </div>
              <div>
                <h3 className="font-black text-gray-900 text-xl uppercase tracking-tight">Media Library</h3>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                  {images.length} {images.length === 1 ? 'file' : 'files'}
                  {mode === 'select' ? ' · Click any image to use it' : ' · Click to preview'}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="bg-white p-2.5 rounded-xl shadow-sm hover:shadow-md transition-all text-gray-400 hover:text-gray-600 border border-gray-100"
            >
              <SafeIcon icon={FiX} className="w-5 h-5" />
            </button>
          </div>

          {/* Toolbar */}
          <div className="px-6 py-3 border-b border-gray-100 flex justify-between items-center bg-white shrink-0">
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-lg transition-all ${viewMode === 'grid' ? 'bg-teal-50 text-teal-600' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'}`}
              >
                <SafeIcon icon={FiGrid} className="w-5 h-5" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-lg transition-all ${viewMode === 'list' ? 'bg-teal-50 text-teal-600' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'}`}
              >
                <SafeIcon icon={FiList} className="w-5 h-5" />
              </button>
            </div>
            {mode === 'select' && (
              <div className="flex items-center space-x-2 px-3 py-1.5 bg-teal-50 border border-teal-200 rounded-xl">
                <SafeIcon icon={FiCheckCircle} className="w-4 h-4 text-teal-500" />
                <span className="text-xs font-bold text-teal-700">Click any image to instantly use it</span>
              </div>
            )}
          </div>

          {/* Image Grid / List */}
          <div className="flex-1 overflow-y-auto p-6 custom-scrollbar">
            {isLoading ? (
              <div className="flex flex-col items-center justify-center h-64 space-y-4">
                <div className="w-10 h-10 border-4 border-teal-200 border-t-teal-500 rounded-full animate-spin" />
                <p className="text-sm font-medium text-gray-400">Loading your library...</p>
              </div>
            ) : images.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 space-y-4 text-center">
                <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center">
                  <SafeIcon icon={FiImage} className="w-8 h-8 text-gray-200" />
                </div>
                <div>
                  <p className="font-black text-gray-400 uppercase tracking-wider text-sm">No files yet</p>
                  <p className="text-xs text-gray-300 mt-1">Upload images from the logo or section settings</p>
                </div>
              </div>
            ) : viewMode === 'grid' ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {images.map((image) => (
                  <motion.div
                    key={image.id}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className={`relative group aspect-square rounded-2xl overflow-hidden cursor-pointer border-4 transition-all ${
                      mode === 'select'
                        ? 'border-transparent hover:border-teal-400 hover:shadow-xl hover:shadow-teal-100'
                        : 'border-transparent hover:border-gray-200'
                    }`}
                    onClick={() => handleImageClick(image)}
                  >
                    {image.url ? (
                      <img
                        src={image.url}
                        alt={image.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div
                      className="w-full h-full bg-gray-100 flex items-center justify-center"
                      style={{ display: image.url ? 'none' : 'flex' }}
                    >
                      <SafeIcon icon={FiPackage} className="w-12 h-12 text-gray-300" />
                    </div>

                    {/* Hover Overlay */}
                    <div className={`absolute inset-0 transition-all ${
                      mode === 'select'
                        ? 'bg-black/0 group-hover:bg-teal-900/40'
                        : 'bg-black/0 group-hover:bg-black/50'
                    }`}>
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        {mode === 'select' ? (
                          <div className="bg-[#c8f542] text-black rounded-full px-4 py-2 font-black text-xs uppercase tracking-widest shadow-xl flex items-center space-x-1.5">
                            <SafeIcon icon={FiCheck} className="w-3.5 h-3.5" />
                            <span>Use Image</span>
                          </div>
                        ) : (
                          <div className="bg-white/90 backdrop-blur-sm rounded-full p-3 shadow-lg">
                            <SafeIcon icon={FiZoomIn} className="w-6 h-6 text-gray-700" />
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Delete Button (manage mode) */}
                    {mode === 'manage' && (
                      <div className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteConfirm(image);
                          }}
                          className="p-2 bg-red-500 text-white rounded-xl hover:bg-red-600 transition-colors shadow-lg"
                        >
                          <SafeIcon icon={FiTrash2} className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )}

                    {/* Folder badge */}
                    {image.folder && image.folder !== 'root' && (
                      <div className="absolute top-2 left-2 bg-black/50 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-md uppercase tracking-wider opacity-0 group-hover:opacity-100 transition-opacity">
                        {image.folder}
                      </div>
                    )}

                    {/* Hover file name */}
                    <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                      <p className="text-white text-xs font-medium truncate">{image.name}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            ) : (
              /* List View */
              <div className="space-y-2">
                {images.map((image) => (
                  <div
                    key={image.id}
                    onClick={() => handleImageClick(image)}
                    className={`flex items-center space-x-4 p-3 rounded-xl cursor-pointer transition-all border ${
                      mode === 'select'
                        ? 'hover:bg-teal-50 border-transparent hover:border-teal-200'
                        : 'hover:bg-gray-50 border-transparent'
                    }`}
                  >
                    <div className="w-14 h-14 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center shrink-0 relative group/thumb">
                      {image.url ? (
                        <img src={image.url} alt={image.name} className="w-full h-full object-cover" />
                      ) : (
                        <SafeIcon icon={FiPackage} className="w-6 h-6 text-gray-300" />
                      )}
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover/thumb:opacity-100 transition-opacity flex items-center justify-center rounded-lg">
                        <SafeIcon
                          icon={mode === 'select' ? FiCheck : FiZoomIn}
                          className="w-4 h-4 text-white"
                        />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-800 truncate text-sm">{image.name}</p>
                      <div className="flex items-center space-x-2 mt-0.5">
                        {image.folder && image.folder !== 'root' && (
                          <span className="text-[10px] text-teal-500 font-bold uppercase">{image.folder}</span>
                        )}
                        <p className="text-xs text-gray-400">{formatDate(image.created_at)}</p>
                        {image.size && (
                          <>
                            <span className="text-gray-300">·</span>
                            <p className="text-xs text-gray-400">{formatFileSize(image.size)}</p>
                          </>
                        )}
                      </div>
                    </div>
                    {mode === 'select' && (
                      <div className="shrink-0 px-3 py-1.5 bg-teal-500 hover:bg-teal-600 text-white rounded-lg text-xs font-black transition-colors flex items-center space-x-1">
                        <SafeIcon icon={FiCheck} className="w-3.5 h-3.5" />
                        <span>Use</span>
                      </div>
                    )}
                    {mode === 'manage' && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteConfirm(image);
                        }}
                        className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors shrink-0"
                      >
                        <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-5 bg-gray-50 border-t border-gray-100 flex space-x-4 shrink-0">
            <button
              onClick={onClose}
              className="flex-1 py-3.5 font-black text-gray-400 hover:text-gray-600 uppercase tracking-widest text-xs transition-colors"
            >
              Cancel
            </button>
            {mode === 'manage' && (
              <button
                onClick={onClose}
                className="flex-[2] py-3.5 bg-gray-900 text-white font-black uppercase tracking-widest text-xs rounded-2xl shadow-xl shadow-gray-200 hover:bg-teal-600 hover:shadow-teal-100 transition-all"
              >
                Done
              </button>
            )}
          </div>

          {/* Delete Confirmation Modal */}
          <AnimatePresence>
            {deleteConfirm && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 bg-black/50 flex items-center justify-center p-4 z-10 rounded-[2rem]"
                onClick={() => setDeleteConfirm(null)}
              >
                <motion.div
                  initial={{ scale: 0.9, y: 20 }}
                  animate={{ scale: 1, y: 0 }}
                  exit={{ scale: 0.9, y: 20 }}
                  className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="p-3 bg-red-100 rounded-xl">
                      <SafeIcon icon={FiAlertCircle} className="w-6 h-6 text-red-500" />
                    </div>
                    <div>
                      <h4 className="font-black text-gray-900">Delete File?</h4>
                      <p className="text-xs text-gray-400">This action cannot be undone</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3 mb-6 p-3 bg-gray-50 rounded-xl">
                    {deleteConfirm.url ? (
                      <img
                        src={deleteConfirm.url}
                        alt=""
                        className="w-12 h-12 object-cover rounded-lg"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                        <SafeIcon icon={FiPackage} className="w-6 h-6 text-gray-300" />
                      </div>
                    )}
                    <p className="text-sm font-medium text-gray-800 truncate flex-1">{deleteConfirm.name}</p>
                  </div>
                  <div className="flex space-x-3">
                    <button
                      onClick={() => setDeleteConfirm(null)}
                      className="flex-1 py-3 font-bold text-gray-500 hover:bg-gray-100 rounded-xl transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => handleDelete(deleteConfirm)}
                      disabled={isDeleting}
                      className="flex-1 py-3 bg-red-500 text-white font-bold rounded-xl hover:bg-red-600 transition-colors flex items-center justify-center space-x-2"
                    >
                      {isDeleting ? (
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <><SafeIcon icon={FiTrash2} className="w-4 h-4" /><span>Delete</span></>
                      )}
                    </button>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>

      {/* Full-screen Image Preview (manage mode only, or select mode with preview) */}
      <AnimatePresence>
        {previewImage && (
          <ImagePreviewModal
            image={previewImage}
            images={images}
            onClose={() => setPreviewImage(null)}
            onDelete={(img) => { setPreviewImage(null); setDeleteConfirm(img); }}
            onNavigate={(img) => setPreviewImage(img)}
            onSelect={handleSelectFromPreview}
            mode={mode}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default LibraryPopup;